import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type { FlowGraphNode } from '../api/contracts';
import { systemLabel } from '../lib/catalogs';
import { formatDateTime } from '../lib/format';

/** Panel del paso seleccionado, con el enlace a sus mensajes. */
export function FlowStepDetail({
  step,
  onClose,
}: {
  step: FlowGraphNode;
  onClose: () => void;
}): ReactElement {
  return (
    <section className="panel">
      <header className="panel__header">
        <h2>
          {systemLabel(step.system)} ·{' '}
          <span className="mono">{step.eventType ?? 'sin evento'}</span>
        </h2>
        <button type="button" className="button" onClick={onClose}>
          Cerrar
        </button>
      </header>
      <div className="panel__body">
        {step.description === null ? null : <p>{step.description}</p>}

        {step.status === 'pending' ? (
          <p className="muted">
            Este paso no ocurrió en la corrida.{' '}
            {step.optional === true
              ? 'El flujo lo declara condicional, así que su ausencia no es necesariamente un problema.'
              : 'El flujo lo declara obligatorio: la corrida quedó incompleta.'}
          </p>
        ) : (
          <>
            <dl className="kv">
              <dt>Mensajes</dt>
              <dd>{step.messageCount}</dd>
              <dt>Fallidos</dt>
              <dd>{step.failedCount}</dd>
              <dt>En cuarentena</dt>
              <dd>{step.quarantinedCount}</dd>
              <dt>Desde</dt>
              <dd>{step.firstAt === null ? '—' : formatDateTime(step.firstAt)}</dd>
              <dt>Duración</dt>
              <dd>{step.durationMs === null ? '—' : `${step.durationMs} ms`}</dd>
            </dl>

            {step.lastError === null ? null : (
              <p className="mono flow-step-detail__error">{step.lastError}</p>
            )}

            {/* La observacion de un resultado esperado (el -4006 de SAP antes de cargar el
                tipo de cambio): informa, no alarma. Por eso no usa el estilo de error. */}
            {step.note === null || step.note === undefined ? null : (
              <p className="flow-step-detail__note">{step.note}</p>
            )}

            <ul>
              {step.messageIds.map((messageId) => (
                <li key={messageId}>
                  <Link to={`/mensajes/${messageId}`}>
                    <span className="mono">{messageId.slice(0, 8)}</span> ver payloads e intentos
                  </Link>
                </li>
              ))}
            </ul>

            {!step.messageIdsTruncated ? null : (
              <p className="muted">
                El paso tiene {step.messageCount} mensajes y acá se listan los primeros{' '}
                {step.messageIds.length}.{' '}
                <Link
                  to={`/mensajes?correlationId=${encodeURIComponent(step.id.split(':')[2] ?? '')}`}
                >
                  Ver todos en el listado
                </Link>
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
